import React, {ReactElement} from 'react';
import {Container, Text} from '@inlet/react-pixi';
import {TextStyle} from 'pixi.js';
import {CoreDI, inject, injectable, ResourceLoader, ScreenParameters, Store} from '@gongxigames/framework/core';
import {ProvideProps, Rectangle} from '@gongxigames/framework/core/fragments/react';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {ResourceManager} from '@gongxigames/framework/slot/services/ResourcesManager';
import {CheatPluginsObjects, DataProps, ICheatsModuleContext} from './CheatModuleInterfaces';
import {ButtonWithTitle} from './components/ButtonWithTitle';
import {DisplayObjectList} from './components/DisplayObjectList';
import {ListSelectedFilters} from './components/ListSelectedFilters';
import {ToggleButtonWithTitle} from './components/ToggleButtonWithTitle';
import {ReactView} from '@gongxigames/framework/slot/fragments/module/view';
import {Container as PixiContainer} from 'pixi.js';
import {slotStore} from '@gongxigames/framework/slotV2/globals';

const style = new TextStyle({
    fontFamily: 'Arial',
    fontSize: 80,
    fontWeight: 'bold',
    align: 'center',
    fill: '#ffffff'
});

type CheatsViewState = {
    replayLastSpinButtonDisabled: boolean;
    currentPanel: string;
    resourceLoaded: boolean;
    width: number;
    height: number;
    moduleContext?: ICheatsModuleContext;
};

type PluginsView = Record<string, JSX.Element>;

@injectable()
export class CheatsView extends ReactView<CheatsViewState> {
    public static readonly NAME: string = 'CheatsView';
    public static layer = LAYERS.TOP;

    @inject(CoreDI.ResourceLoader)
    protected _resourceLoader!: ResourceLoader;

    @inject(DI.ResourceManager)
    protected _resourceManager!: ResourceManager;

    protected _pluginsView: PluginsView = {};
    protected _plugins: CheatPluginsObjects = {};
    protected offEffects: (() => any)[] = [];

    public state: CheatsViewState = {
        replayLastSpinButtonDisabled: false,
        currentPanel: 'main',
        resourceLoaded: true,
        width: 0,
        height: 0
    };

    protected get parent(): PixiContainer {
        if (slotStore.slot_isGameStateMachineActive.value) {
            return slotStore.stage_rootContainer_ui.value;

        }

        return this._system.app.stageV1;
    }

    public init(): void {
        super.init();
        this.container.name = CheatsView.NAME.toString();

        this.on('moduleInitialized', this.moduleInitialized.bind(this), this);
    }

    public createReactMediator(): void {
        super.createReactMediator();

        if (slotStore.slot_isGameStateMachineActive.value) {
            this.container.scale.set(0.5);
            const off = Store.effect(() => {
                const safeZone = slotStore.stage_safeZoneState_ui.value;
                const screenBounds = safeZone.calcInSafeFader();

                this.setState({
                    height: screenBounds.height * 2,
                    width: screenBounds.width * 2
                });
            });

            this.offEffects.push(off);
        }
    }

    public removeEvents(): void {
        super.removeEvents();

        this.off('moduleInitialized', this);

        const {moduleContext} = this.state!;

        if (!moduleContext) {
            return;
        }

        const {storage} = moduleContext;

        Object.keys(storage.data).forEach(key => {
            storage.off(key as keyof DataProps, this);
        });

        this.offEffects.forEach(off => off());
    }

    public moduleInitialized(data: { moduleContext: ICheatsModuleContext; plugins: CheatPluginsObjects; }): void {
        const {storage} = data.moduleContext;

        Object.keys(storage.data).forEach(key => {
            storage.on(key as keyof DataProps, this.forceUpdate.bind(this), this);
        });

        Object.keys(data.plugins).forEach(name => {
            const plugin = data.plugins[name];

            this._pluginsView[name] = React.createElement(plugin.view);
            this._plugins[name] = plugin;
        });

        this.setState({moduleContext: data.moduleContext});
    }

    protected setVisible(visible: boolean): void {
        this.state.moduleContext!.storage.set('visibleView', visible);
    }

    protected toggleVisible(): void {
        this.setVisible(!this.isVisibleContainer());
    }

    public onResized(screenParams: ScreenParameters): void {
        if (!slotStore.slot_isGameStateMachineActive.value) {
            const screenBounds = screenParams.getScreenBounds();
            const state = {
                height: screenBounds.height,
                width: screenBounds.width
            };

            this.container.x = screenBounds.x;
            this.container.y = screenBounds.y;

            this.setState(state);
        }
    }

    protected toggleStats(value: boolean): void {
        this.state.moduleContext!.storage.set('statsEnabled', value);
    }

    protected toggleRewind(value: boolean): void {
        this.state.moduleContext!.storage.set('rewindVisible', value);
    }

    protected setCheatsInfoState(value: boolean): void {
        this.state.moduleContext!.storage.set('cheatsInfoEnabled', value);
    }

    protected setReplayLastSpinEnabled(value: boolean): void {
        this.state.moduleContext!.storage.set('replayLastSpinEnabled', value);
    }

    protected setPanel(panel: string): void {
        this.setState({currentPanel: panel});
    }

    protected getPage(): ReactElement<{}> | null {
        if (this.state.currentPanel === 'main') {
            return this.mainPanel();
        } else {
            const plugin = this._plugins[this.state.currentPanel];
            const pluginView = this._pluginsView[this.state.currentPanel];

            if (plugin && pluginView) {
                const pluginProps = {
                    controller: plugin.controller
                };

                return (
                    <ProvideProps {...pluginProps} >
                        {pluginView}
                    </ProvideProps>
                );
            }

            return null;
        }
    }

    protected mainPanel(): JSX.Element {
        const pages = Object.keys(this._pluginsView);
        const {statsEnabled, rewindVisible, cheatsInfoEnabled, replayLastSpinEnabled} = this.state.moduleContext!.storage.data;
        const buttons = pages.map(name => {
            return <ButtonWithTitle
                key={name}
                title={name}
                onClick={this.setPanel.bind(this, name)}
                width={940}
                height={280}
                fontSize={100}
            />;
        });

        buttons.push(
            <ToggleButtonWithTitle
                key={'Toggle Stats'}
                title={'Toggle Stats'}
                onChange={this.toggleStats.bind(this)}
                width={940}
                height={280}
                fontSize={100}
                value={statsEnabled}
            />,
            <ToggleButtonWithTitle
                key={'Toggle Rewind'}
                title={'Toggle Rewind'}
                onChange={this.toggleRewind.bind(this)}
                width={940}
                height={280}
                fontSize={100}
                value={rewindVisible}
            />,
            <ToggleButtonWithTitle
                key={'ToggleCheatsInfo'}
                title={`Selected filters\n(${cheatsInfoEnabled ? 'shown' : 'hidden'})`}
                onChange={this.setCheatsInfoState.bind(this)}
                width={940}
                height={280}
                fontSize={100}
                value={cheatsInfoEnabled}
            />,
            <ToggleButtonWithTitle
                key={'ReplayLastSpin'}
                title={'Replay Last Spin'}
                onChange={this.setReplayLastSpinEnabled.bind(this)}
                disabled={this.state.replayLastSpinButtonDisabled}
                width={940}
                height={280}
                fontSize={100}
                value={replayLastSpinEnabled}
            />
        );

        return (
            <DisplayObjectList direction={'column'} gapBetweenChildren={20} wrap={this.state.height - 500}>
                {buttons}
            </DisplayObjectList>
        );
    }

    protected getHeadMenu(): JSX.Element {
        let backButton = null;

        const MenuButton = (title: string, onClick: () => void): JSX.Element => {
            return <ButtonWithTitle
                title={title}
                key={title}
                onClick={onClick}
                width={'auto'}
                height={150}
                fontSize={100}
                padding={200}
            />;
        };

        if (this.state.currentPanel !== 'main') {
            backButton = MenuButton('Back', this.setPanel.bind(this, 'main'));
        }

        return (
            <DisplayObjectList y={120} direction={'row'} gapBetweenChildren={20} >
                {backButton}
                {MenuButton('Close', this.toggleVisible.bind(this))}
            </DisplayObjectList>
        );
    }

    protected getMainLayer(): JSX.Element {
        const title = (this.state.currentPanel === 'main' ? 'Debug tool' : this.state.currentPanel).toUpperCase();
        const {cheatsInfoEnabled} = this.state.moduleContext!.storage.data;

        return (
            <React.Fragment>
                <Container visible={this.isVisibleContainer()}>
                    <Rectangle width={this.state.width} height={this.state.height} fill={0x000000} alpha={0.5} />
                    <Container x={20}>
                        <Text style={style} text={title} y={20} />
                        {this.getHeadMenu()}
                        <Container y={300}>
                            {this.getPage()}
                        </Container>
                    </Container>
                </Container>
                <ProvideProps moduleContext={this.state.moduleContext} eventsManager={this}>
                    <Container visible={cheatsInfoEnabled}>
                        <ListSelectedFilters x={this.state.width}/>
                    </Container>
                </ProvideProps>
            </React.Fragment>
        );
    }

    protected getLoadingLayer(): JSX.Element {
        return (
            <Container visible={this.isVisibleContainer()}>
                <Rectangle width={this.state.width} height={this.state.height} fill={0x000000} alpha={0.5} />
                <Text style={style} text={'LOADING...'} x={this.state.width / 2} y={this.state.height / 2} anchor={0.5}/>
            </Container>
        );
    }

    render(): JSX.Element {
        const isLoading = !this.state.moduleContext || !this.state.resourceLoaded;

        return (
            <Container name={CheatsView.NAME.toString()} >
                {isLoading ? this.getLoadingLayer() : this.getMainLayer()}
            </Container>
        );

    }

    protected isVisibleContainer(): boolean {
        return !!this.state.moduleContext && this.state.moduleContext.storage.get('visibleView');
    }
}
